import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Satellite,
  ShieldCheck,
  Radio,
  Cpu,
  Lock,
  Camera,
  Bell,
  Award,
  Sparkles,
  ArrowRight,
  Check,
  Phone,
  Mail,
} from "lucide-react";
import PayPalCheckoutButton from "@/components/PayPalCheckoutButton";

const PRODUCTS = [
  {
    id: "shield",
    testId: "product-card-shield",
    name: "Sriman Shield",
    tagline: "Not Just Security. Real Protection.",
    category: "Home & Enterprise Protection",
    image:
      "https://customer-assets-39nsmqrw.emergentagent.net/job_sriman-enterprise/artifacts/voloso7n_IMG-20260412-WA0004.jpg",
    imageAlt: "Sriman Shield — Satellite protection console",
    description:
      "A satellite-connected protection system that goes beyond monitoring. Sriman Shield actively detects threats, alerts you, and safeguards your home, family and assets — even when the internet is down.",
    price: "₹3,49,000",
    offerPrice: "₹2,99,999",
    offerPriceUSD: "$3,599",
    offerLabel: "Pre-booking offer · first 50 customers",
    features: [
      { icon: Satellite, title: "Satellite Connectivity", text: "Works even without internet — direct-to-satellite uplink." },
      { icon: Radio, title: "Anti-Radiation Design", text: "100% safe offline protection with certified shielding." },
      { icon: Cpu, title: "Smart Sensor Technology", text: "Detects. Alerts. Protects. — motion, gas, noise, air quality." },
      { icon: Lock, title: "Secure & Reliable", text: "Built for ultimate safety and peace of mind." },
    ],
    highlights: [
      "Legal Agreement Attested",
      "No price hikes for the next 20 years",
      "Airtime costs rise — your price stays the same",
      "1 Year Free Subscription included",
      "2-Year Buy-Back Guarantee — 40% refund if returned within 2 years",
    ],
    subscription:
      "From 2nd year onwards: actual airtime ₹36,000+GST — you pay only ₹12,000+GST. Sriman Solutions absorbs the remaining ₹24,000+GST.",
  },
  {
    id: "d",
    testId: "product-card-d",
    name: "Sriman D",
    tagline: "D for Defence — a wearable personal protection pendant.",
    category: "Personal Defence · Wearable",
    image:
      "https://customer-assets-39nsmqrw.emergentagent.net/job_sriman-enterprise/artifacts/4x7p2tav_1781897066078.png",
    imageAlt: "Sriman D — Sky Blue titanium defence pendant",
    description:
      "A discreet, satellite-connected titanium pendant engineered for personal defence. Zero-radiation shielding, GNSS positioning, hidden SOS activation and a high-decibel deterrent — designed to travel with you, anywhere.",
    price: "₹89,999",
    offerPrice: "₹49,999",
    offerPriceUSD: "$599",
    offerLabel: "Pre-launch offer · first 50 users",
    features: [
      { icon: Satellite, title: "GNSS Antenna Array", text: "Specialized, micro-engraved ceramic GPS antenna for precise positioning." },
      { icon: Camera, title: "Pinhole Micro-Camera", text: "Tiny lens, compact optics for discreet capture." },
      { icon: Bell, title: "Hidden SOS Trigger", text: "Dynamic hidden activation button — no accidental triggers." },
      { icon: Award, title: "Mu-Metal Shielding", text: "0.001% zero-radiation edge — safe against your skin." },
    ],
    highlights: [
      "Sky Blue-finished titanium build",
      "50-meter high-decibel speaker deterrent",
      "Sriman Deep Tech pressure points",
      "3D pendant · satellite connectivity",
      "6 months free subscription · ₹349/month thereafter",
      "Individually serialised & attested",
    ],
    subscription:
      "Subscription: ₹349 / month — first 6 months free. Pre-launch batch limited to the first 50 units; post-launch pricing reverts to ₹89,999.",
  },
];

const ProductBlock = ({ product, index }) => {
  const reversed = index % 2 === 1;
  return (
    <section
      id={product.id}
      data-testid={product.testId}
      className={`py-20 md:py-28 border-b border-white/5 ${
        index % 2 === 0 ? "bg-[#040812]" : "bg-[#060B1A]"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div
          className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start ${
            reversed ? "lg:[&>*:first-child]:order-2" : ""
          }`}
        >
          {/* Image */}
          <div className="lg:col-span-6">
            <div className="relative border border-white/10 overflow-hidden bg-[#0B132B]/50">
              <img
                src={product.image}
                alt={product.imageAlt}
                className="w-full h-auto object-contain"
              />
            </div>
          </div>

          {/* Details */}
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3">
              <span className="font-mono text-[11px] text-red-500 tracking-[0.28em]">
                /0{index + 1}
              </span>
              <span className="overline !text-slate-400 !text-[10px]">
                {product.category}
              </span>
            </div>

            <h2 className="mt-5 font-display text-white text-4xl md:text-5xl lg:text-6xl tracking-tighter font-light leading-[1.05]">
              {product.name}
            </h2>
            <p className="mt-4 text-red-500 font-display text-lg md:text-xl">
              {product.tagline}
            </p>

            <p className="mt-6 text-slate-400 text-base md:text-lg leading-relaxed">
              {product.description}
            </p>

            {/* Price block */}
            <div className="mt-8 bg-[#0B132B]/60 border border-white/10 p-6 md:p-7 flex flex-col sm:flex-row sm:items-end gap-6">
              <div>
                <div className="overline mb-2">Price</div>
                <div className="font-display text-slate-400 text-2xl md:text-3xl line-through decoration-red-500/60 decoration-2">
                  {product.price}
                </div>
              </div>
              <div className="sm:border-l sm:border-white/10 sm:pl-6">
                <div className="overline mb-2 !text-red-500">
                  {product.offerLabel}
                </div>
                <div
                  data-testid={`${product.testId}-offer-price`}
                  className="font-display text-white text-4xl md:text-5xl tracking-tight"
                >
                  {product.offerPrice}
                </div>
                <div className="mt-2 font-mono text-[11px] text-slate-400 tracking-widest">
                  ≈ {product.offerPriceUSD} USD · charged via PayPal
                </div>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 text-xs text-slate-500 font-mono tracking-widest">
              <Sparkles size={12} className="text-red-500" />
              Limited quantity · availability confirmed on pre-booking
            </div>

            {/* CTAs */}
            <div className="mt-8">
              <div className="bg-[#0B132B]/60 border border-white/10 p-4 md:p-5 rounded-sm">
                <PayPalCheckoutButton
                  productId={product.id}
                  productName={product.name}
                  testId={`${product.testId}-cta`}
                />
              </div>
              <div className="mt-4 flex flex-col sm:flex-row gap-3">
                <a
                  href="tel:+919701503764"
                  className="inline-flex items-center justify-center gap-2 border border-white/15 hover:border-white text-white text-sm px-6 py-3 rounded-sm transition-colors"
                >
                  <Phone size={14} />
                  +91-9701503764
                </a>
                <a
                  href={`mailto:Info@srimansolutions.com?subject=Pre-booking%20${encodeURIComponent(product.name)}`}
                  className="inline-flex items-center justify-center gap-2 border border-white/15 hover:border-white text-white text-sm px-6 py-3 rounded-sm transition-colors"
                >
                  <Mail size={14} />
                  Email us
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {product.features.map(({ icon: Icon, title, text }) => (
            <div
              key={title}
              className="bg-[#0B132B]/50 border border-white/10 p-6 hover-lift"
            >
              <Icon size={18} className="text-red-500" strokeWidth={1.5} />
              <div className="mt-5 font-display text-white text-lg tracking-tight">
                {title}
              </div>
              <div className="mt-3 text-slate-400 text-sm leading-relaxed">
                {text}
              </div>
            </div>
          ))}
        </div>

        {/* Highlights */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-7 bg-[#0B132B]/50 border border-white/10 p-8">
            <div className="overline mb-4">What&apos;s included</div>
            <ul className="space-y-3">
              {product.highlights.map((h) => (
                <li
                  key={h}
                  className="flex items-start gap-3 text-slate-300 text-sm md:text-base border-b border-white/5 pb-3 last:border-0 last:pb-0"
                >
                  <Check
                    size={16}
                    className="text-red-500 mt-1 shrink-0"
                    strokeWidth={2}
                  />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-5 bg-[#0B132B]/50 border border-white/10 p-8">
            <div className="overline mb-4">Subscription & terms</div>
            <p className="text-slate-300 text-sm md:text-base leading-relaxed">
              {product.subscription}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const COMPARISON_ROWS = [
  { label: "Best for", shield: "Homes, offices, showrooms & assets you can't leave unattended", d: "You, personally — when you're travelling, commuting or alone" },
  { label: "Form factor", shield: "Wall-mounted satellite console + sensors", d: "Wearable titanium pendant · Sky Blue finish" },
  { label: "Connectivity", shield: "Direct-to-satellite uplink · works when the internet is down", d: "Satellite + GNSS positioning · anywhere on earth" },
  { label: "Detection", shield: "Motion, gas, noise, air quality — 24×7 sensor grid", d: "Hidden SOS trigger + pinhole micro-camera on demand" },
  { label: "Safety", shield: "Certified anti-radiation shielding", d: "Mu-metal shielding · 0.001% zero-radiation edge · skin-safe" },
  { label: "Warranty & buy-back", shield: "2-year buy-back — 40% refund if returned", d: "Individually serialised, attested build" },
  { label: "Subscription", shield: "1 year free · ₹12k+GST/yr thereafter (Sriman absorbs ₹24k+GST)", d: "First 6 months free · ₹349/month thereafter" },
  { label: "Pre-booking price", shield: "₹2,99,999 · $3,599", d: "₹49,999 · $599", highlight: true },
  { label: "List price after launch", shield: "₹3,49,000", d: "₹89,999" },
];

const ProductComparison = () => (
  <section
    id="compare"
    data-testid="product-comparison"
    className="py-20 md:py-28 border-b border-white/5 bg-[#040812]"
  >
    <div className="max-w-6xl mx-auto px-6 lg:px-10">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <span className="overline">/Compare</span>
          <h2 className="mt-4 font-display text-white text-4xl md:text-5xl tracking-tighter font-light leading-tight">
            Shield or D — which fits you?
          </h2>
          <p className="mt-4 text-slate-400 text-base max-w-2xl">
            A quick side-by-side. Most customers end up owning both — one for the home, one for the person.
          </p>
        </div>
        <div className="flex gap-3">
          <a
            href="#shield"
            className="inline-flex items-center gap-2 border border-white/15 hover:border-red-500 text-white text-sm px-4 py-2.5 rounded-sm transition-colors"
          >
            <ShieldCheck size={14} className="text-red-500" /> Jump to Shield
          </a>
          <a
            href="#d"
            className="inline-flex items-center gap-2 border border-white/15 hover:border-red-500 text-white text-sm px-4 py-2.5 rounded-sm transition-colors"
          >
            <Award size={14} className="text-red-500" /> Jump to D
          </a>
        </div>
      </div>

      <div className="mt-10 bg-[#0B132B]/50 border border-white/10 overflow-hidden">
        {/* Header row */}
        <div className="grid grid-cols-3 border-b border-white/10">
          <div className="p-5 font-mono text-[10px] text-slate-500 tracking-[0.24em] uppercase">
            Feature
          </div>
          <div className="p-5 border-l border-white/10">
            <div className="font-mono text-[10px] text-red-500 tracking-[0.24em] uppercase">
              Home & Enterprise
            </div>
            <div className="mt-1 font-display text-white text-2xl md:text-3xl tracking-tight">
              Sriman Shield
            </div>
          </div>
          <div className="p-5 border-l border-white/10">
            <div className="font-mono text-[10px] text-red-500 tracking-[0.24em] uppercase">
              Personal · Wearable
            </div>
            <div className="mt-1 font-display text-white text-2xl md:text-3xl tracking-tight">
              Sriman D
            </div>
          </div>
        </div>

        {/* Rows */}
        {COMPARISON_ROWS.map((row) => (
          <div
            key={row.label}
            className={`grid grid-cols-3 border-b border-white/5 last:border-0 ${
              row.highlight ? "bg-red-500/5" : ""
            }`}
          >
            <div className="p-5 font-mono text-[10px] text-slate-400 tracking-[0.22em] uppercase self-center">
              {row.label}
            </div>
            <div className="p-5 border-l border-white/10 text-sm text-slate-200 leading-relaxed">
              {row.shield}
            </div>
            <div className="p-5 border-l border-white/10 text-sm text-slate-200 leading-relaxed">
              {row.d}
            </div>
          </div>
        ))}

        {/* CTA row */}
        <div className="grid grid-cols-3 border-t border-white/10 bg-[#040812]/60">
          <div className="p-5" />
          <div className="p-5 border-l border-white/10">
            <a
              href="#shield"
              data-testid="compare-cta-shield"
              className="inline-flex items-center justify-center w-full gap-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium px-4 py-3 rounded-sm transition-colors"
            >
              Pre-book Shield
              <ArrowRight size={14} />
            </a>
          </div>
          <div className="p-5 border-l border-white/10">
            <a
              href="#d"
              data-testid="compare-cta-d"
              className="inline-flex items-center justify-center w-full gap-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium px-4 py-3 rounded-sm transition-colors"
            >
              Pre-book Sriman D
              <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default function Products() {
  return (
    <main data-testid="products-page" className="bg-[#040812]">
      {/* Header */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-24 border-b border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-red-500/10 blur-3xl rounded-full pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <span className="overline">Sriman Solutions · Products</span>
          <h1 className="mt-6 font-display text-white text-5xl md:text-6xl lg:text-7xl tracking-tighter font-light leading-[1.02] max-w-4xl">
            Hardware built for{" "}
            <span className="text-red-500">real protection</span>,
            not just monitoring.
          </h1>
          <p className="mt-8 text-slate-400 text-base md:text-lg max-w-2xl leading-relaxed">
            Two flagship devices from the Sriman Solutions Innovation Lab —
            satellite-connected, radiation-safe, and engineered to keep working
            when everything else stops.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-start gap-3">
            <a
              href="#shield"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-red-500 text-white text-sm px-5 py-3 rounded-sm transition-colors"
            >
              <ShieldCheck size={14} className="text-red-500" />
              Sriman Shield
            </a>
            <a
              href="#d"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-red-500 text-white text-sm px-5 py-3 rounded-sm transition-colors"
            >
              <Award size={14} className="text-red-500" />
              Sriman D
            </a>
          </div>
        </div>
      </section>

      {PRODUCTS.map((p, i) => (
        <ProductBlock key={p.id} product={p} index={i} />
      ))}

      {/* Side-by-side comparison */}
      <ProductComparison />

      {/* Bottom CTA */}
      <section className="py-24 md:py-32 bg-[#040812]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <span className="overline">Reserve your unit</span>
          <h2 className="mt-6 font-display text-white text-4xl md:text-5xl tracking-tighter font-light leading-tight">
            Only 50 pre-booking slots per product.
          </h2>
          <p className="mt-6 text-slate-400 text-base md:text-lg">
            Pre-book directly with the founding team. Delivery, KYC and
            installation coordinated personally.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/contact"
              data-testid="products-bottom-cta"
              className="group inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white font-medium px-7 py-4 rounded-sm transition-colors"
            >
              Reserve now
              <ArrowRight
                size={16}
                className="transition-transform group-hover:translate-x-1"
              />
            </Link>
            <a
              href="mailto:Info@srimansolutions.com?subject=Product%20pre-booking%20inquiry"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-white text-white text-sm px-6 py-4 rounded-sm transition-colors"
            >
              <Mail size={14} />
              Info@srimansolutions.com
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
